import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Check, Undo2, EyeOff, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/kontoauszug`;
const eur = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const de = (s) => (s && s.length >= 10 ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : s || "–");
const TABS = [["pruefen", "Bitte prüfen"], ["gebucht", "Gebucht"], ["erledigt", "Schon erfasst"], ["offen", "Nicht zugeordnet"], ["ignoriert", "Ignoriert"]];
const fehler = (err, t) => toast.error(err.response?.data?.detail || t);

const Zuordnen = ({ u, onDone }) => {
  const [nr, setNr] = useState("");
  const buchen = async (body) => {
    try { const { data } = await axios.post(`${API}/umsaetze/${u.id}/zuordnen`, body); toast.success(`Auf ${data.rechnungsnummer} gebucht`); onDone(); }
    catch (err) { fehler(err, "Zuordnung fehlgeschlagen"); }
  };
  const ignorieren = async () => {
    try { await axios.post(`${API}/umsaetze/${u.id}/ignorieren`, {}); toast.success("Ignoriert"); onDone(); } catch (err) { fehler(err, "Fehlgeschlagen"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {(u.vorschlaege || []).map((v) => (
        <Button key={v.id} size="sm" onClick={() => buchen({ rechnung_id: v.id })} data-testid={`ka-passt-${u.id}-${v.nr}`}>
          <Check size={14} className="mr-1" />Passt: {v.nr} ({eur(v.offen)} offen)
        </Button>
      ))}
      <Input value={nr} onChange={(e) => setNr(e.target.value)} placeholder="DC-2026-…" className="h-8 w-36" data-testid={`ka-nr-input-${u.id}`} />
      <Button size="sm" variant="outline" disabled={!nr.trim()} onClick={() => buchen({ rechnungsnummer: nr })} data-testid={`ka-zuordnen-btn-${u.id}`}>Zuordnen</Button>
      <Button size="sm" variant="ghost" onClick={ignorieren} title="Keine Kursgebühr" data-testid={`ka-ignorieren-btn-${u.id}`}><EyeOff size={14} /></Button>
    </div>
  );
};

const Zeile = ({ u, onDone }) => {
  const zurueck = async () => {
    if (!window.confirm("Zuordnung aufheben? Die Zahlung wird von der Rechnung wieder entfernt.")) return;
    try { await axios.post(`${API}/umsaetze/${u.id}/rueckgaengig`); toast.success("Aufgehoben"); onDone(); } catch (err) { fehler(err, "Fehlgeschlagen"); }
  };
  return (
    <div className="border-t border-stone-100 px-4 py-3 grid gap-2 sm:grid-cols-[110px_1fr_120px]" data-testid={`ka-zeile-${u.id}`}>
      <div className="text-sm text-stone-600">{de(u.datum)}<div className="text-xs text-stone-400" data-testid={`ka-konto-${u.id}`}>{u.konto}</div></div>
      <div>
        <div className="font-medium text-stone-900">{u.name}</div>
        <div className="text-sm text-stone-600">{u.zweck || "–"}</div>
        {u.hinweis && <div className="text-xs text-amber-700 mt-0.5">{u.hinweis}</div>}
        <div className="mt-2">
          {u.status === "gebucht" ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700">
              <Check size={14} />{u.rechnungsnummer} · {u.automatisch ? "automatisch" : u.gebucht_von}
              <Button size="sm" variant="ghost" onClick={zurueck} data-testid={`ka-rueckgaengig-btn-${u.id}`}><Undo2 size={14} className="mr-1" />Rückgängig</Button>
            </div>
          ) : u.status === "erledigt" ? (
            <div className="flex items-center gap-2 text-sm text-stone-600" data-testid={`ka-erledigt-${u.id}`}><Check size={14} />{u.rechnungsnummer}
              <Button size="sm" variant="ghost" onClick={zurueck} data-testid={`ka-wieder-pruefen-btn-${u.id}`}><Undo2 size={14} className="mr-1" />Doch nicht erfasst</Button></div>
          ) : u.status === "ignoriert" ? (
            <Button size="sm" variant="ghost" onClick={zurueck} data-testid={`ka-reaktivieren-btn-${u.id}`}><Undo2 size={14} className="mr-1" />Wieder anzeigen</Button>
          ) : <Zuordnen u={u} onDone={onDone} />}
        </div>
      </div>
      <div className="text-right font-semibold tabular-nums whitespace-nowrap text-emerald-800">{eur(u.betrag)}</div>
    </div>
  );
};

export default function Kontoauszuege() {
  const [tab, setTab] = useState("pruefen");
  const [d, setD] = useState({ umsaetze: [], anzahl: {} });
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const aktuell = useRef(tab);
  const laden = useCallback(() => {
    aktuell.current = tab;
    return axios.get(`${API}/umsaetze`, { params: { status: tab } })
      .then((r) => { if (aktuell.current === tab) setD(r.data); })
      .catch(() => toast.error("Liste konnte nicht geladen werden"));
  }, [tab]);
  useEffect(() => { laden(); }, [laden]);

  const hochladen = async (files) => {
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data: s } = await axios.post(`${API}/import`, fd);
        toast.success(`${f.name}: ${s.auszuege} Auszug/Auszüge, ${s.neu - s.ausgaben} Zahlungseingänge – ${s.gebucht} automatisch gebucht, ${s.erledigt} schon im CRM erfasst, ${s.pruefen} zum Prüfen, ${s.offen} nicht zugeordnet${s.doppelt ? `, ${s.doppelt} schon vorhanden` : ""}`, { duration: 9000 });
      } catch (err) { fehler(err, `${f.name}: Import fehlgeschlagen`); }
    }
    setBusy(false);
    if (input.current) input.current.value = "";
    laden();
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl" data-testid="kontoauszuege-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-stone-900">Kontoauszüge</h1>
          <p className="text-sm text-stone-600 mt-1 max-w-2xl">Kontoauszug von FINOM oder Sparkasse (PDF) oder den kompletten Beleg-Export (ZIP) hochladen. Zahlungen mit Rechnungsnummer und passendem Betrag werden sofort gebucht, schon eingetragene Zahlungen werden erkannt und nicht doppelt gebucht, alles andere landet unter „Bitte prüfen“.</p>
        </div>
        <input ref={input} type="file" multiple accept=".pdf,.zip" className="hidden" onChange={(e) => hochladen([...e.target.files])} data-testid="ka-file-input" />
        <Button onClick={() => input.current?.click()} disabled={busy} data-testid="ka-upload-btn"><Upload size={16} className="mr-2" />{busy ? "Wird gelesen…" : "Auszug hochladen"}</Button>
      </div>
      <div className="flex flex-wrap gap-2 border-b border-stone-200 mb-4">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ka-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === k ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"}`}>
            {l} <span className="ml-1 rounded-full bg-stone-100 px-2 text-xs" data-testid={`ka-anzahl-${k}`}>{d.anzahl[k] || 0}</span>
          </button>
        ))}
      </div>
      {d.umsaetze.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 p-10 text-center text-stone-500" data-testid="ka-leer"><Landmark className="mx-auto mb-2" />Hier ist nichts.</div>
      ) : (
        <div className="rounded-xl border border-stone-200 bg-white" data-testid="ka-liste">{d.umsaetze.map((u) => <Zeile key={u.id} u={u} onDone={laden} />)}</div>
      )}
    </div>
  );
}
